import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import type { Locale } from 'date-fns'
import { ca as caLocale } from 'date-fns/locale/ca'
import { de as deLocale } from 'date-fns/locale/de'
import { enGB as enLocale } from 'date-fns/locale/en-GB'
import { es as esLocale } from 'date-fns/locale/es'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { useNotification } from '@last/core-ui/v3/plugins/notification.ts'

import { useAuthStore } from '@/store/auth'
import { useConfigStore } from '@/store/config.ts'

export const useLayoutStore = defineStore('layout', () => {
  const configStore = useConfigStore()
  const { billingStatus } = storeToRefs(configStore)

  const authStore = useAuthStore()
  const { user } = storeToRefs(authStore)

  const showPermissionsErrorOnNextRoute = ref<boolean>(false)

  const isMenuExpanded = ref<boolean>(false)

  function toggleMenu(): void {
    if (isMenuExpanded.value && isDesktop.value) return
    isMenuExpanded.value = !isMenuExpanded.value
  }

  function closeMenu(): void {
    if (!isDesktop.value) {
      isMenuExpanded.value = false
    }
  }

  function openMenu(): void {
    isMenuExpanded.value = true
  }

  const isMenuDisabled = ref<boolean>(false)

  const isProfileEditorOpen = ref<boolean>(false)

  function toggleProfileEditor(): void {
    isProfileEditorOpen.value = !isProfileEditorOpen.value
  }

  function openProfileEditor(): void {
    isProfileEditorOpen.value = true
  }

  function closeProfileEditor(): void {
    isProfileEditorOpen.value = false
  }

  const isAssistantOpen = ref<boolean>(false)

  function toggleAssistant(): void {
    isAssistantOpen.value = !isAssistantOpen.value
  }

  function openAssistant(): void {
    isAssistantOpen.value = true
  }

  function closeAssistant(): void {
    isAssistantOpen.value = false
  }

  const isMobile = useBreakpoints(breakpointsTailwind).smaller('sm')
  const isTablet = useBreakpoints(breakpointsTailwind).between('sm', 'md')
  const isSmallDesktop = useBreakpoints(breakpointsTailwind).between('md', 'lg')
  const isDesktop = useBreakpoints(breakpointsTailwind).greaterOrEqual('lg')
  const currentBreakpoint = useBreakpoints(breakpointsTailwind).current()
  watch(
    currentBreakpoint,
    () => {
      if (isMobile.value) {
        isMenuExpanded.value = false
      } else if (isTablet.value) {
        isMenuExpanded.value = false
      } else if (isDesktop.value || isSmallDesktop.value) {
        isMenuExpanded.value = true
      }
    },
    { immediate: true }
  )

  const { locale } = useI18n()

  const dateLocales: Record<string, Locale> = {
    ['es']: esLocale as unknown as Locale,
    ['ca']: caLocale as unknown as Locale,
    ['de']: deLocale as unknown as Locale,
    ['en']: enLocale as unknown as Locale
  }

  const currentDateLocale = computed<Locale>((): Locale => dateLocales[locale.value as string])

  watch(
    () => billingStatus.value,
    status => (isMenuDisabled.value = status.installation && !user.value.isSupport)
  )

  const router = useRouter()
  const notification = useNotification()
  const { t } = useI18n()

  router.afterEach(() => {
    if (showPermissionsErrorOnNextRoute.value) {
      notification.create({
        title: t('notifications.access-denied.title'),
        subtitle: t('notifications.access-denied.description'),
        icon: 'close',
        type: 'error'
      })
      showPermissionsErrorOnNextRoute.value = false
    }
  })

  return {
    showPermissionsErrorOnNextRoute,

    isMobile,
    isTablet,

    isMenuExpanded,
    toggleMenu,
    closeMenu,
    openMenu,

    isMenuDisabled,

    isProfileEditorOpen,
    toggleProfileEditor,
    openProfileEditor,
    closeProfileEditor,

    isAssistantOpen,
    toggleAssistant,
    openAssistant,
    closeAssistant,

    currentDateLocale
  }
})
